import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Edit2, ArrowLeft, Save, LayoutTemplate, Package } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { API } from "@/lib/api";


// ============ LIST PAGE ============
export default function Templates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/templates/technical-sheets`);
      setTemplates(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/templates/technical-sheets/${deleteId}`);
      toast.success("Template excluido!");
      setTemplates((prev) => prev.filter((t) => t.id !== deleteId));
    } catch { toast.error("Erro ao excluir template"); }
    finally { setDeleteId(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            Templates
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Gerencie seus templates de fichas tecnicas</p>
        </div>
        <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => navigate("/templates/new")} data-testid="new-template-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <LayoutTemplate className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400 mb-4">Nenhum template cadastrado</p>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => navigate("/templates/new")}>
              <Plus className="w-4 h-4 mr-2" /> Criar primeiro template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    <th className="text-left p-4 font-medium">Nome</th>
                    <th className="text-center p-4 font-medium">Itens</th>
                    <th className="text-right p-4 font-medium">Produto Principal</th>
                    <th className="text-right p-4 font-medium">Insumos</th>
                    <th className="text-right p-4 font-medium">Total</th>
                    <th className="text-center p-4 font-medium w-28">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="p-4">
                        <p className="font-medium text-slate-900 dark:text-white">{t.name}</p>
                        {t.description && <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>}
                      </td>
                      <td className="p-4 text-center font-mono">{t.items?.length || 0}</td>
                      <td className="p-4 text-right font-mono">{formatCurrency(t.main_product_cost || 0)}</td>
                      <td className="p-4 text-right font-mono">{formatCurrency(t.supplies_cost || 0)}</td>
                      <td className="p-4 text-right font-mono font-bold">{formatCurrency(t.total_cost || 0)}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => navigate(`/templates/${t.id}`)} data-testid={`edit-template-${t.id}`}>
                            <Edit2 className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteId(t.id)} data-testid={`delete-template-${t.id}`}>
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Template</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza? Esta acao nao pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============ FORM PAGE ============
export function TemplateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [formData, setFormData] = useState({ name: "", description: "", items: [{ name: "", quantity: 1, unit: "un", unit_cost: 0 }] });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
    if (isEditing) fetchTemplate();
  }, [id]);

  const fetchProducts = async () => {
    try { const res = await axios.get(`${API}/products`); setProducts(res.data); } catch {}
  };

  const fetchTemplate = async () => {
    try {
      const res = await axios.get(`${API}/templates/technical-sheets/${id}`);
      setFormData({ name: res.data.name, description: res.data.description || "", items: res.data.items || [] });
    } catch { toast.error("Erro ao carregar template"); navigate("/templates"); }
    finally { setLoading(false); }
  };

  const addItem = () => setFormData((prev) => ({ ...prev, items: [...prev.items, { name: "", quantity: 1, unit: "un", unit_cost: 0 }] }));

  const removeItem = (idx) => {
    if (formData.items.length <= 1) return;
    setFormData((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const updateItem = (idx, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const selectProduct = (idx, productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      updateItem(idx, "name", product.name);
      updateItem(idx, "unit", product.unit);
      updateItem(idx, "unit_cost", product.unit_cost);
    }
  };

  const totalCost = formData.items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error("Nome do template e obrigatorio"); return; }
    const validItems = formData.items.filter((i) => i.name?.trim() && parseFloat(i.quantity) > 0);
    if (validItems.length === 0) { toast.error("Adicione pelo menos um item"); return; }

    setSaving(true);
    try {
      const payload = { name: formData.name, description: formData.description, items: validItems };
      if (isEditing) {
        await axios.put(`${API}/templates/technical-sheets/${id}`, payload);
        toast.success("Template atualizado!");
      } else {
        await axios.post(`${API}/templates/technical-sheets`, payload);
        toast.success("Template criado!");
      }
      navigate("/templates");
    } catch { toast.error("Erro ao salvar template"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="template-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/templates")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            {isEditing ? "Editar Template" : "Novo Template"}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Informacoes</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome do Template</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Botox, Preenchimento..." data-testid="input-template-name" />
              </div>
              <div className="space-y-2">
                <Label>Descricao (opcional)</Label>
                <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Descricao breve" data-testid="input-template-description" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>
                Itens ({formData.items.length}) - Total: {formatCurrency(totalCost)}
              </CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>Produto/Insumo</TableHead>
                    <TableHead className="w-20">Qtd</TableHead>
                    <TableHead className="w-20">Unidade</TableHead>
                    <TableHead className="w-28">Custo Unit.</TableHead>
                    <TableHead className="w-28 text-right">Subtotal</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.items.map((item, idx) => {
                    const subtotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0);
                    return (
                      <TableRow key={idx} className={idx === 0 ? "bg-teal-50/50 dark:bg-teal-900/10" : ""}>
                        <TableCell className="font-mono text-xs text-slate-400">{idx === 0 ? "P" : idx}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <select
                              className="h-9 rounded-md border border-input bg-background px-2 text-sm w-12 shrink-0"
                              onChange={(e) => selectProduct(idx, e.target.value)}
                              value=""
                            >
                              <option value="">...</option>
                              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                            <Input
                              value={item.name}
                              onChange={(e) => updateItem(idx, "name", e.target.value)}
                              placeholder="Nome do item"
                              className="min-w-[140px]"
                              data-testid={`item-name-${idx}`}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number" min="0" step="any"
                            value={item.quantity}
                            onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                            className="editable-field"
                          />
                        </TableCell>
                        <TableCell>
                          <Input value={item.unit} onChange={(e) => updateItem(idx, "unit", e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number" min="0" step="0.01"
                            value={item.unit_cost}
                            onChange={(e) => updateItem(idx, "unit_cost", e.target.value)}
                            className="editable-field"
                          />
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">{formatCurrency(subtotal)}</TableCell>
                        <TableCell>
                          {formData.items.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)}>
                              <Trash2 className="w-4 h-4 text-rose-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={saving} data-testid="save-template-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Salvando..." : "Salvar Template"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/templates")}>Cancelar</Button>
        </div>
      </form>
    </div>
  );
}
